import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { GIFT_RESOURCE_ICONS } from '../../constants/giftResourceIcons'
import type { RootState } from '../../store'
import styles from './ResourceSpendFlyLayer.module.scss'

/**
 * Resource-icon CONSUMPTION burst — the exact inverse of the mining/claim effect.
 *
 * Mining (ResourceClaimFlyLayer): icons fly FROM the player's click INTO the top-bar
 * counter. Consumption (this layer): icons lift off the top-bar counter (the resource
 * marker) and arc/converge INTO the consumption point — the player's last click, i.e.
 * the Upgrade / Start / Research / Buy button they just pressed. It reads as resources
 * being *invested* (warm/golden, with an "absorb" sparkle on arrival), not destroyed.
 *
 * Flow: any watched Redux balance decreases (ResourceSpendWatcher) — or a site calls
 * flyResourceSpend() directly for spends that don't reach Redux at action time — and a
 * burst launches FROM the matching on-screen counter (located by `data-resource-balance`
 * so the source is each page's own board: main land, diplomacy, espionage, world-map)
 * toward the consumption point. No-ops when the counter is off-screen or there was no
 * recent click to fly toward (passive drains are not animated).
 */

export type SpendResourceType = 'ECS' | 'WHEAT' | 'BREAD' | 'WOOD' | 'STONE' | 'IRON' | 'WATER' | 'USDT' | 'ENERGY'

type Rect = { left: number; top: number; width: number; height: number }

type SpendParticle = {
  id: string
  iconUrl: string
  sx: number
  sy: number
  mx: number
  my: number
  ex: number
  ey: number
  delay: number
  duration: number
  size: number
}

type SpendBatch = {
  id: string
  particles: SpendParticle[]
  sparkle: { x: number; y: number; delay: number } | null
}

const FLY_DURATION_MS = 760
/** Only fly toward a consumption point if the click that caused it is this recent. */
const POINTER_FRESH_MS = 7000

// USDT lives on several boards but isn't a reward key, so it's absent from
// GIFT_RESOURCE_ICONS — add it here so USDT spends can animate too.
const SPEND_ICONS: Record<string, string> = {
  ...GIFT_RESOURCE_ICONS,
  usdt: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mining-factory/usdt-icon.webp',
  energy: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/energy/energy-icon.webp',
}

// ── last-pointer tracking: the consumption point the resources fly INTO ─────────
let lastPointer = { x: 0, y: 0, t: -Infinity }
/**
 * A click that opened a confirm dialog. While set (and recent), the spend flies to HERE —
 * the button the user actually pressed (e.g. "Immediate") — instead of the dialog's OK
 * button, which would otherwise be the most recent click. Cleared by the next real
 * pointerdown so it never leaks into an unrelated, direct spend.
 */
let pendingConsumptionPoint: { x: number; y: number; t: number } | null = null
/**
 * The centre of an action button (marked `data-spend-origin`) captured AT PRESS TIME. The
 * resulting spend flies to HERE regardless of any clicks that follow — a later click elsewhere,
 * or one/many confirm-dialog OK presses — so e.g. the "Immediate" effect always lands on the
 * Immediate button inside the building, never on the dialog or a stray click. Overridden only by
 * the next action-button press; auto-clears shortly after it is used (with a TTL backstop).
 */
let lockedOrigin: { x: number; y: number; t: number; usedAt: number } | null = null
let pointerTracking = false
function ensurePointerTracking() {
  if (pointerTracking || typeof window === 'undefined') return
  pointerTracking = true
  window.addEventListener(
    'pointerdown',
    (e) => {
      lastPointer = { x: e.clientX, y: e.clientY, t: Date.now() }
      const originEl = (e.target as HTMLElement | null)?.closest?.('[data-spend-origin]') as HTMLElement | null
      if (originEl) {
        // Pressing an action button pins ITS centre as the consumption point for the spend it
        // triggers. This is the authoritative origin: it survives the confirm OK click(s) and any
        // later click, so the effect cannot drift off the button the user actually pressed.
        const r = originEl.getBoundingClientRect()
        lockedOrigin = { x: r.left + r.width / 2, y: r.top + r.height / 2, t: Date.now(), usedAt: 0 }
      }
      // A fresh, direct interaction defines its own consumption point — drop any pinned confirm
      // one. (lockedOrigin is intentionally NOT cleared here: a confirm's OK click, or a stray
      // click after the press, must not move the origin off the action button.)
      pendingConsumptionPoint = null
    },
    { capture: true, passive: true },
  )
}
ensurePointerTracking()

// ── pub/sub so the imperative flyResourceSpend() reaches the mounted layer ──────
type Listener = (batch: SpendBatch) => void
const listeners = new Set<Listener>()
function emit(batch: SpendBatch) {
  listeners.forEach((fn) => fn(batch))
}

function findTargetEl(key: string): HTMLElement | null {
  if (typeof document === 'undefined') return null
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[data-resource-balance="${key}"]`))
  // A hidden responsive/off-route copy reports a zero-size rect — prefer the visible one.
  const visible = nodes.find((el) => {
    const r = el.getBoundingClientRect()
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight
  })
  return visible ?? nodes[0] ?? null
}

/** Pinned consumption points stay valid this long after a confirm resolves — long enough to
 *  cover the OK → API → balance-update round-trip; cleared sooner by the next pointerdown. */
const PENDING_CONSUMPTION_TTL_MS = 8000
/** A button-locked origin is valid this long after the press — generous enough to cover a slow
 *  read of a confirm dialog before OK, then the API + balance round-trip. */
const LOCKED_ORIGIN_TTL_MS = 15000
/** Once a locked origin is first used it clears after this grace, so one action that spends
 *  several resources keeps a single origin, yet the lock can't leak into a later, unrelated spend. */
const LOCKED_ORIGIN_USE_GRACE_MS = 1200

/** The point the resources fly into = where the spend was initiated.
 *  Priority: (1) a button-locked origin captured at press time (authoritative — immune to later
 *  clicks and confirm dialogs); (2) a pinned pre-confirm click; (3) the last click, if recent. */
function resolveConsumptionPoint(override: Rect | null): Rect | null {
  if (override) return override
  const now = Date.now()
  if (lockedOrigin && now - lockedOrigin.t < LOCKED_ORIGIN_TTL_MS) {
    if (!lockedOrigin.usedAt) {
      lockedOrigin.usedAt = now
      const captured = lockedOrigin
      setTimeout(() => {
        if (lockedOrigin === captured) lockedOrigin = null
      }, LOCKED_ORIGIN_USE_GRACE_MS)
    }
    return { left: lockedOrigin.x, top: lockedOrigin.y, width: 0, height: 0 }
  }
  if (pendingConsumptionPoint && now - pendingConsumptionPoint.t < PENDING_CONSUMPTION_TTL_MS) {
    return { left: pendingConsumptionPoint.x, top: pendingConsumptionPoint.y, width: 0, height: 0 }
  }
  const fresh = now - lastPointer.t < POINTER_FRESH_MS
  if (!fresh) return null
  return { left: lastPointer.x, top: lastPointer.y, width: 0, height: 0 }
}

/**
 * Snapshot the current click as the origin for an action about to be gated behind a confirm
 * dialog. Returns null when there's no recent click. Pair with applyConsumptionOrigin on
 * confirm so the eventual spend flies to the button the user pressed, not the OK button.
 */
export function captureConsumptionOrigin(): { x: number; y: number } | null {
  if (Date.now() - lastPointer.t > POINTER_FRESH_MS) return null
  return { x: lastPointer.x, y: lastPointer.y }
}

/** Pin a previously captured origin so the next spend flies to it (call when a confirm is
 *  accepted, after the OK click has already moved the live pointer to the dialog). */
export function applyConsumptionOrigin(origin: { x: number; y: number } | null | undefined): void {
  if (!origin) return
  pendingConsumptionPoint = { x: origin.x, y: origin.y, t: Date.now() }
}

function buildParticles(from: Rect, to: Rect, iconUrl: string, count: number, seed: string): SpendParticle[] {
  // Launch from the counter (the resource marker), converge into the consumption point.
  const sx = from.left + from.width / 2
  const sy = from.top + from.height / 2
  const ex = to.left + to.width / 2
  const ey = to.top + to.height / 2
  const n = Math.max(7, Math.min(count, 16))

  return Array.from({ length: n }, (_, i) => {
    const t = i / Math.max(n - 1, 1)
    const spread = 0.22
    const arcLift = -56 - (i % 3) * 18
    const mx = sx + (ex - sx) * (0.42 + t * 0.16) + (i % 2 === 0 ? -1 : 1) * spread * Math.abs(ex - sx) * 0.28
    const my = sy + (ey - sy) * 0.42 + arcLift
    const jitterX = (Math.random() - 0.5) * 26
    const jitterY = (Math.random() - 0.5) * 16

    return {
      id: `rsf-${seed}-${i}`,
      iconUrl,
      sx: sx + jitterX - (i % 3) * 3,
      sy: sy + jitterY,
      mx: mx + jitterX * 0.5,
      my: my + jitterY * 0.4,
      ex: ex + (Math.random() - 0.5) * 16,
      ey: ey + (Math.random() - 0.5) * 12,
      delay: i * 36,
      duration: FLY_DURATION_MS,
      size: 16 + (i % 4) * 4,
    }
  })
}

function maxMs(particles: SpendParticle[]): number {
  return particles.reduce((m, p) => Math.max(m, p.delay + p.duration), 0)
}

/** Soft positive lift on the counter as the resources leave it (no shrink, no red). */
function pulseSource(el: HTMLElement) {
  if (typeof el.animate !== 'function') return
  try {
    el.animate(
      [
        { transform: 'scale(1)', filter: 'brightness(1)' },
        { transform: 'scale(1.06)', filter: 'brightness(1.3) drop-shadow(0 0 6px rgba(255, 225, 150, 0.85))' },
        { transform: 'scale(1)', filter: 'brightness(1)' },
      ],
      { duration: 420, easing: 'ease-out' },
    )
  } catch {
    /* decorative — ignore */
  }
}

let spendCounter = 0

/** When a spend last fired, per resource key — coalesces optimistic debit + reconcile. */
const lastSpendAt: Record<string, number> = {}

function flyInternal(resourceType: SpendResourceType, destOverride: Rect | null): void {
  if (typeof document === 'undefined') return
  const key = resourceType.toLowerCase()
  lastSpendAt[key] = Date.now()
  const iconUrl = SPEND_ICONS[key]
  if (!iconUrl) return

  const sourceEl = findTargetEl(key)
  if (!sourceEl) return

  const dest = resolveConsumptionPoint(destOverride)
  if (!dest) return // no recent click to fly toward → passive change, don't animate

  const from = sourceEl.getBoundingClientRect()
  spendCounter += 1
  const seed = `${spendCounter}-${Date.now()}`
  const particles = buildParticles(
    { left: from.left, top: from.top, width: from.width, height: from.height },
    dest,
    iconUrl,
    11,
    seed,
  )
  if (particles.length === 0) return

  const sparkle = { x: dest.left + dest.width / 2, y: dest.top + dest.height / 2, delay: Math.max(0, maxMs(particles) - 220) }
  emit({ id: `sbatch-${seed}`, particles, sparkle })
  pulseSource(sourceEl)
}

/**
 * Fire a resource-icon consumption burst FROM the matching board counter INTO the
 * player's last click (the spend action). Safe to call from anywhere; no-ops when the
 * counter is off-screen or there is no recent click. Call this directly at spend sites
 * that don't push the new balance into Redux (so the watcher can't see them).
 */
export function flyResourceSpend(resourceType: SpendResourceType): void {
  flyInternal(resourceType, null)
}

/**
 * Like flyResourceSpend, but flies toward an explicit screen coordinate instead of
 * the last click. Use for energy spends where the destination is the player's castle
 * (known position, no click involved).
 */
export function flyResourceSpendTo(resourceType: SpendResourceType, dest: { x: number; y: number }): void {
  flyInternal(resourceType, { left: dest.x, top: dest.y, width: 0, height: 0 })
}

/** Bigger, slower icons for the energy bounce (vs the 16px / 760ms generic spend burst). */
const ENERGY_BOUNCE_DURATION_MS = 1150

/**
 * Energy-spend bounce: energy icons lift off the energy counter, dip a little BELOW it,
 * then rise back UP to it — used instead of flying toward the castle. The icons are a
 * little bigger and the motion a little slower than the generic spend burst.
 */
export function flyEnergySpendBounce(): void {
  if (typeof document === 'undefined') return
  lastSpendAt['energy'] = Date.now() // dedup the ENERGY balance-decrease watcher
  const iconUrl = SPEND_ICONS['energy']
  if (!iconUrl) return
  const sourceEl = findTargetEl('energy')
  if (!sourceEl) return

  const r = sourceEl.getBoundingClientRect()
  const cx = r.left + r.width / 2
  const cy = r.top + r.height / 2

  spendCounter += 1
  const seed = `energy-${spendCounter}-${Date.now()}`
  const n = 6
  const particles: SpendParticle[] = Array.from({ length: n }, (_, i) => {
    const jitterX = (Math.random() - 0.5) * 34
    const downOffset = 60 + (i % 3) * 14 // dip below the counter
    const upOffset = 18 + (i % 2) * 10 // then rise slightly above it
    return {
      id: `esb-${seed}-${i}`,
      iconUrl,
      sx: cx + jitterX * 0.35,
      sy: cy,
      mx: cx + jitterX,
      my: cy + downOffset,
      ex: cx + jitterX * 0.5,
      ey: cy - upOffset,
      delay: i * 70,
      duration: ENERGY_BOUNCE_DURATION_MS,
      size: 30 + (i % 3) * 5,
    }
  })

  emit({ id: `sbatch-${seed}`, particles, sparkle: null })
  pulseSource(sourceEl)
}

// ── balance-decrease watcher (mirror of ResourceGainWatcher) ────────────────────
/** Coalesce an optimistic local debit with its follow-up server reconcile (same spend). */
const SPEND_COALESCE_MS = 1500

/** Redux user resource fields → spend type (mirrors the gain watcher's list + usdt). */
const WATCHED_RESOURCE_FIELDS: Array<{ field: string; type: SpendResourceType }> = [
  { field: 'water', type: 'WATER' },
  { field: 'wheat', type: 'WHEAT' },
  { field: 'bread', type: 'BREAD' },
  { field: 'wood', type: 'WOOD' },
  { field: 'stone', type: 'STONE' },
  { field: 'iron', type: 'IRON' },
  { field: 'ecsTokenAmount', type: 'ECS' },
  { field: 'usdt', type: 'USDT' },
  { field: 'energy', type: 'ENERGY' },
]

/**
 * Per-resource mute for the automatic decrease-watcher, keyed by lowercase resource.
 * Set by callers whose spend should stay silent (see suppressResourceSpendFly).
 */
const suppressSpendUntil: Record<string, number> = {}

/**
 * Mute the automatic consumption burst for one resource for a short window.
 *
 * The watcher fires on ANY Redux balance decrease, which is the right default — but a few
 * spends are an implementation detail of an action the player didn't frame as "spending"
 * (starting tower mining costs water; starting a well costs gems), and animating those reads
 * as the building leaking resources. Call this immediately before the balance reaches Redux.
 *
 * Only affects the automatic watcher — an explicit flyResourceSpend() call still animates.
 */
export function suppressResourceSpendFly(resourceType: SpendResourceType, ms = 3000): void {
  suppressSpendUntil[resourceType.toLowerCase()] = Date.now() + ms
}

function autoFlySpend(resourceType: SpendResourceType): void {
  const key = resourceType.toLowerCase()
  const now = Date.now()
  if (now < (suppressSpendUntil[key] || 0)) return // muted by the acting site
  if (now - (lastSpendAt[key] || 0) < SPEND_COALESCE_MS) return // dedup optimistic + reconcile
  if (now - lastPointer.t > POINTER_FRESH_MS) return // skip passive drains (no recent action)
  flyResourceSpend(resourceType)
}

/**
 * Mounted once, globally. Watches the player's Redux resource balances and fires a
 * consumption burst whenever one decreases, so every spend path that reaches Redux
 * (research, upgrades, swaps, purchases, …) shows the effect with no per-site wiring.
 * Spends that don't reach Redux at action time call flyResourceSpend() directly.
 */
export const ResourceSpendWatcher: React.FC = () => {
  const signature = useSelector((state: RootState) => {
    const u = state.userModule?.user as Record<string, unknown> | undefined
    return WATCHED_RESOURCE_FIELDS.map((r) => Number(u?.[r.field] ?? 0)).join('|')
  })
  const prevRef = useRef<number[] | null>(null)

  useEffect(() => {
    const curr = signature.split('|').map(Number)
    const prev = prevRef.current
    if (prev) {
      WATCHED_RESOURCE_FIELDS.forEach((r, i) => {
        if ((prev[i] ?? 0) - curr[i] > 1e-6) autoFlySpend(r.type)
      })
    }
    prevRef.current = curr
  }, [signature])

  return null
}

const ResourceSpendFlyLayer: React.FC = () => {
  const [batches, setBatches] = useState<SpendBatch[]>([])

  useEffect(() => {
    ensurePointerTracking()
    const listener: Listener = (batch) => {
      setBatches((prev) => [...prev, batch])
      const ttl = maxMs(batch.particles) + 360
      window.setTimeout(() => {
        setBatches((prev) => prev.filter((b) => b.id !== batch.id))
      }, ttl)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  if (typeof document === 'undefined' || batches.length === 0) return null

  return createPortal(
    <div className={styles.layer} aria-hidden>
      {batches.map((b) => (
        <React.Fragment key={b.id}>
          {b.particles.map((p) => (
            <img
              key={p.id}
              src={p.iconUrl}
              alt=""
              draggable={false}
              className={styles.particle}
              style={
                {
                  width: p.size,
                  height: p.size,
                  ['--sp-sx']: `${p.sx - p.size / 2}px`,
                  ['--sp-sy']: `${p.sy - p.size / 2}px`,
                  ['--sp-mx']: `${p.mx - p.size / 2}px`,
                  ['--sp-my']: `${p.my - p.size / 2}px`,
                  ['--sp-ex']: `${p.ex - p.size / 2}px`,
                  ['--sp-ey']: `${p.ey - p.size / 2}px`,
                  ['--sp-dur']: `${p.duration}ms`,
                  ['--sp-delay']: `${p.delay}ms`,
                } as React.CSSProperties
              }
            />
          ))}
          {b.sparkle && (
            <span
              className={styles.sparkle}
              style={
                {
                  ['--sp-lx']: `${b.sparkle.x}px`,
                  ['--sp-ly']: `${b.sparkle.y}px`,
                  ['--sp-delay']: `${b.sparkle.delay}ms`,
                } as React.CSSProperties
              }
            />
          )}
        </React.Fragment>
      ))}
    </div>,
    document.body,
  )
}

export default ResourceSpendFlyLayer
